import { useEffect, useMemo, useState } from "react"

import axios from "axios"
import useSWR, { useSWRConfig } from "swr"
import _ from "lodash"
import moment from "moment"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { useNavigate } from "react-router-dom"

import ErrorMessage from "components/forms/error-message"
import Description from "components/forms/description"
import Label from "components/forms/label"
import Input from "components/forms/input"
import Checkbox from "components/forms/checkbox"
import InputDate from "components/forms/input-date"
import SelectDescription from "components/forms/select-description"
import { X, ArrowNarrowLeft, ArrowNarrowRight } from "components/icons/outline"
import { lang } from "config"

import { useAuth } from "contexts/auth-context"
import { useAuditLogContext } from "contexts/audit-log-context"
import useBranchRepositories from "repositories/branch"
import useDiscountRepositories from "repositories/discount"
import ImportTarget from "./import"

const fetcher = async (url, params) => {
    return await axios.get(url, { params }).then((res) => res.data.data)
}

const conflictsFetcher = async (url, body) => {
    return await axios.post(url, body).then((res) => res.data.data)
}

const levelSelection = [
    { key: "type", label: "Tipe Produk" },
    { key: "brand", label: "Merek Produk" },
    { key: "series", label: "Seri Produk" },
    { key: "model", label: "Model Produk" },
    { key: "unit", label: "Unit Produk" },
]

// Field harga yang dipotong diskon.
const applyToSelection = [
    { key: "price", label: "Price (HET / Retail)" },
    { key: "store_price", label: "Store Price (Harga Toko)" },
    { key: "both", label: "Keduanya (Price & Store Price)" },
]

const PER_PAGE = 20

export default function Form() {
    const { user, isSuper, currentBranch } = useAuth()
    const { cache } = useSWRConfig()
    const { queueAuditLog } = useAuditLogContext()
    const navigate = useNavigate()
    const isSup = isSuper()

    // Step 1: Outlet
    const [outletId, setOutletId] = useState(isSup ? null : currentBranch?.id ?? null)

    const { data: branchSelection, isLoading: isLoadingBranchSelection } = useBranchRepositories({
        paginate: false,
    })

    const filteredBranchSelection = useMemo(() => {
        if (!branchSelection) return []
        return branchSelection.filter((item) => (isSup ? true : item.id === currentBranch?.id))
    }, [branchSelection, isSup, currentBranch])

    // Step 2: Discount (filtered by outlet)
    const [discount, setDiscount] = useState()
    const [discountObject, setDiscountObject] = useState()
    const [applyTo, setApplyTo] = useState("price")

    const { data: discountSelection, isLoading: isLoadingDiscountSelection } = useDiscountRepositories(
        outletId
            ? {
                  paginate: false,
                  order_by: "name",
                  direction: "asc",
                  outlet_id: outletId,
              }
            : null
    )

    useEffect(() => {
        setDiscount(undefined)
        setDiscountObject(undefined)
    }, [outletId])

    useEffect(() => {
        if (discount && discountSelection) {
            setDiscountObject(discountSelection.find((row) => row.id === discount))
        } else {
            setDiscountObject(undefined)
        }
    }, [discount, discountSelection])

    // Step 3: Level
    const [level, setLevel] = useState()

    // Step 4: Target (inline multi-select)
    const [target, setTarget] = useState([])
    const [searchValue, setSearchValue] = useState("")
    const [search, setSearch] = useState("")
    const [page, setPage] = useState(1)

    // Import daftar produk dari file (CSV/XLSX)
    const [importResult, setImportResult] = useState(null)

    useEffect(() => {
        setTarget([])
        setSearchValue("")
        setSearch("")
        setPage(1)
        setImportResult(null)
    }, [level])

    const debouncedSetSearch = useMemo(
        () =>
            _.debounce((val) => {
                setPage(1)
                setSearch(val)
            }, 400),
        []
    )

    useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch])

    const onSearchChange = (val) => {
        setSearchValue(val)
        debouncedSetSearch(val)
    }

    const productBaseUrl = `${process.env.REACT_APP_INVENTORY_API_URL}/product/${level ?? ""}`
    const orderBy = level === "brand" ? "brand.name" : level === "series" ? "series.name" : "name"

    const listParams = useMemo(
        () => ({
            paginate: true,
            page,
            per_page: PER_PAGE,
            search,
            order_by: orderBy,
            direction: "asc",
        }),
        [page, search, orderBy]
    )

    const { data: productList, isLoading: isLoadingProductList } = useSWR(
        level ? [productBaseUrl, listParams] : null,
        fetcher,
        { revalidateOnFocus: false }
    )

    const rows = productList?.data ?? []
    const totalPages = productList?.last_page ?? 1
    const total = productList?.total ?? 0

    // Dates
    const [from, setFrom] = useState()
    const [to, setTo] = useState()

    // Conflict check: highlight rows that already have an overlapping discount
    // during [from, to]. Fires once outlet + period + level are set. Checks
    // both visible rows and currently-selected targets so changing dates after
    // picking can also surface conflicts on items not on the current page.
    const checkIds = useMemo(() => {
        const ids = new Set(rows.map((r) => r.id))
        target.forEach((t) => ids.add(t.id))
        return Array.from(ids)
    }, [rows, target])

    const conflictsKey =
        outletId && level && from && to && checkIds.length
            ? [
                  `${process.env.REACT_APP_INVENTORY_API_URL}/discount/conflicts`,
                  { level, target: checkIds, from, to, outlet_id: outletId },
              ]
            : null

    const { data: conflictsData, error: conflictsError, isLoading: isLoadingConflicts } = useSWR(
        conflictsKey,
        conflictsFetcher,
        { revalidateOnFocus: false, keepPreviousData: false }
    )

    const conflictsMap = conflictsData ?? {}
    const conflictCount = Object.keys(conflictsMap).length

    const conflictCheckActive = !!conflictsKey
    const conflictCheckPending = !outletId
        ? "Pilih outlet dahulu"
        : !from || !to
        ? "Isi periode (dari/sampai) untuk cek konflik"
        : !level
        ? "Pilih level untuk cek konflik"
        : null

    const getRowConflicts = (row) => conflictsMap[String(row.id)] ?? null
    const isRowConflicted = (row) => !!getRowConflicts(row)

    const targetTitle = (row) => {
        // Baris hasil import hanya punya {id, name}; fallback ke row.name.
        if (level === "brand") return row.brand?.name ?? row.name
        if (level === "series") return row.series?.name ?? row.name
        return row.name
    }

    const targetDescription = (row) => {
        if (level === "brand") return row.product_type?.name
        if (level === "series") return row.product_brand?.brand?.name
        return row.long_name
    }

    // Import dari file: item sudah diresolusi ({id, name}) oleh ImportTarget.
    // Tambahkan ke target (skip duplikat & yang konflik periode).
    const handleImportItems = (items, meta) => {
        const existing = new Set(target.map((t) => String(t.id)))
        const toAdd = items.filter(
            (m) => !existing.has(String(m.id)) && !conflictsMap[String(m.id)]
        )
        setTarget((prev) => [
            ...prev,
            ...toAdd.map((m) => ({ id: m.id, name: m.name, _imported: true })),
        ])
        setImportResult({
            added: toAdd.length,
            skipped: items.length - toAdd.length,
            unmatchedCount: meta?.unmatchedCount ?? 0,
        })
    }

    const isSelected = (row) => target.some((t) => t.id === row.id)

    const toggleOne = (row) => {
        if (isRowConflicted(row)) return
        if (isSelected(row)) {
            setTarget(target.filter((t) => t.id !== row.id))
        } else {
            setTarget([...target, row])
        }
    }

    const selectableRows = useMemo(() => rows.filter((r) => !isRowConflicted(r)), [rows, conflictsMap])

    const allVisibleSelected =
        selectableRows.length > 0 && selectableRows.every(isSelected)

    const toggleAllVisible = () => {
        if (allVisibleSelected) {
            setTarget(target.filter((t) => !selectableRows.some((r) => r.id === t.id)))
        } else {
            const newOnes = selectableRows.filter((r) => !isSelected(r))
            setTarget([...target, ...newOnes])
        }
    }

    const removeTarget = (id) => {
        setTarget(target.filter((t) => t.id !== id))
    }

    // Submit
    const [errors, setErrors] = useState({})

    const handleSubmit = (e) => {
        e.preventDefault()

        if (nProgress.isStarted()) {
            toast.error("Aksi sebelumnya sedang dimuat")
            return
        }

        toast.loading("Memuat", { id: "submit" })
        toast.dismiss("conflict_data")

        const url = `${process.env.REACT_APP_INVENTORY_API_URL}/discount/apply`
        const targetIds = target.map((row) => row.id)

        const auditLogPayload = {
            outlet_id: outletId,
            level,
            discount_id: discount,
            discount: discountObject?.name,
            apply_to: applyTo,
            from,
            to,
            target: target.map((row) => ({
                [`product_${level}_id`]: row.id,
                [`product_${level}`]: targetTitle(row),
            })),
        }

        const formPayload = {
            level,
            target: targetIds,
            discount,
            outlet_id: outletId,
            apply_to: applyTo,
            from,
            to,
        }

        axios
            .post(url, formPayload)
            .then((response) => {
                queueAuditLog(
                    url,
                    response.config.method.toUpperCase(),
                    "Apply Discount Data",
                    user?.name,
                    auditLogPayload,
                    null,
                    moment().format("YYYY-MM-DD HH:mm:ss"),
                    response.status
                )

                cache.clear()
                navigate({ pathname: "/discount" })

                toast.success(response.data.message, { id: "submit" })
            })
            .catch((error) => {
                if (error.response?.status === 422) {
                    setErrors(error.response.data.errors)
                }

                if (error.response?.status === 400) {
                    const names = error.response.data.data.map((row, i) =>
                        i + 1 !== error.response.data.data.length ? `${row}, ` : row
                    )
                    toast.error(names, {
                        ariaProps: { superscript: "Data Konflik" },
                        id: "conflict_data",
                    })
                }

                toast.error(error.response?.data?.message ?? "Terjadi kesalahan", {
                    ariaProps: { superscript: error.response?.status },
                    id: "submit",
                })
            })
    }

    const stepDisabled = {
        discount: !outletId,
        dates: !outletId || !discount,
        level: !outletId || !discount,
        target: !outletId || !discount || !level,
    }

    return (
        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
            {/* Step 1 — Outlet */}
            <FormSection title="Outlet" description="Pilih outlet tempat diskon akan diterapkan.">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="outlet" value="Outlet" />
                        <SelectDescription
                            onChange={(value) => setOutletId(value)}
                            selection={filteredBranchSelection}
                            isLoading={isLoadingBranchSelection}
                            value={outletId}
                            keyValue={(value) => value.id}
                            title={(value) => value.name}
                            description={(value) => value.code}
                            placeholder="Pilih Outlet"
                            error={errors.outlet_id}
                            disabled={!isSup}
                        />
                        <ErrorMessage error={errors.outlet_id} />
                    </div>
                </div>
            </FormSection>

            {/* Step 2 — Discount + Dates */}
            <FormSection
                title="Discount"
                description="Pilih diskon yang terdaftar di outlet terpilih, dan tentukan periode."
            >
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="discount" value="Discount" />
                        <SelectDescription
                            onChange={(value) => setDiscount(value)}
                            selection={discountSelection ?? []}
                            isLoading={isLoadingDiscountSelection}
                            value={discount}
                            keyValue={(value) => value.id}
                            title={(value) => value.name}
                            placeholder={outletId ? "Pilih Discount" : "Pilih Outlet Terlebih Dahulu"}
                            error={errors.discount}
                            disabled={stepDisabled.discount}
                        />
                        <ErrorMessage error={errors.discount} />
                    </div>
                    <div>
                        <Label htmlFor="apply_to" value="Terapkan ke Harga" />
                        <SelectDescription
                            onChange={(value) => setApplyTo(value)}
                            selection={applyToSelection}
                            isLoading={false}
                            value={applyTo}
                            keyValue={(value) => value.key}
                            title={(value) => value.label}
                            placeholder="Pilih Field Harga"
                            error={errors.apply_to}
                            disabled={stepDisabled.discount}
                        />
                        <ErrorMessage error={errors.apply_to} />
                    </div>
                    <div>
                        <Label htmlFor="from" value="Dari" />
                        <InputDate
                            id="from"
                            error={errors.from}
                            maxDate={Date.parse(to)}
                            onChange={(value) => setFrom(moment(value).format("Y-MM-DD"))}
                            value={Date.parse(from)}
                            selected={Date.parse(from)}
                            disabled={stepDisabled.dates}
                        />
                        <ErrorMessage error={errors.from} />
                    </div>
                    <div>
                        <Label htmlFor="to" value="Sampai" />
                        <InputDate
                            id="to"
                            error={errors.to}
                            minDate={Date.parse(from)}
                            onChange={(value) => setTo(moment(value).format("Y-MM-DD"))}
                            value={Date.parse(to)}
                            selected={Date.parse(to)}
                            disabled={stepDisabled.dates}
                        />
                        <ErrorMessage error={errors.to} />
                    </div>
                </div>
            </FormSection>

            {/* Step 3 — Level & Target */}
            <FormSection
                title="Target Produk"
                description="Pilih level produk lalu centang produk-produk yang akan menerima diskon."
            >
                <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label htmlFor="level" value="Level" />
                            <SelectDescription
                                onChange={(value) => setLevel(value)}
                                selection={levelSelection}
                                isLoading={false}
                                value={level}
                                keyValue={(value) => value.key}
                                title={(value) => value.label}
                                placeholder={discount ? "Pilih Level" : "Pilih Discount Terlebih Dahulu"}
                                error={errors.level}
                                disabled={stepDisabled.level}
                            />
                            <ErrorMessage error={errors.level} />
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                            <div className="flex-1">
                                <Label value="Cari Produk" />
                                <Input
                                    placeholder={level ? `Cari produk ${level}...` : "Pilih level terlebih dahulu"}
                                    value={searchValue}
                                    onChange={(e) => onSearchChange(e.target.value)}
                                    disabled={stepDisabled.target}
                                />
                            </div>
                            <div className="flex items-center gap-3 text-xs text-neutral-600">
                                <span>
                                    {target.length} terpilih
                                    {total > 0 && ` dari ${total} produk`}
                                </span>
                            </div>
                        </div>

                        {/* Import daftar produk dari file (CSV/XLSX) */}
                        {level && (
                            <div className="p-4 space-y-2 border border-dashed border-neutral-300 rounded-xl bg-neutral-50">
                                <div className="flex items-center justify-between">
                                    <Label value={`Import Daftar Produk (${level})`} />
                                    <span className="text-xs text-neutral-500">
                                        Kolom <b>id</b>, atau <b>sku</b>/<b>name</b>
                                    </span>
                                </div>
                                <ImportTarget
                                    level={level}
                                    disabled={stepDisabled.target}
                                    onImport={handleImportItems}
                                />
                                {importResult && (
                                    <div className="text-xs text-neutral-600">
                                        {importResult.added} ditambahkan
                                        {importResult.skipped > 0 && (
                                            <span className="text-amber-700"> · {importResult.skipped} dilewati (duplikat/konflik)</span>
                                        )}
                                        {importResult.unmatchedCount > 0 && (
                                            <span className="text-red-700"> · {importResult.unmatchedCount} tidak ditemukan</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {level && (
                            <div
                                className={`flex items-center justify-between gap-3 px-4 py-2 text-xs rounded-xl border ${
                                    conflictsError
                                        ? "bg-red-50 border-red-200 text-red-700"
                                        : conflictCheckPending
                                        ? "bg-amber-50 border-amber-200 text-amber-800"
                                        : isLoadingConflicts
                                        ? "bg-neutral-50 border-neutral-200 text-neutral-600"
                                        : conflictCount > 0
                                        ? "bg-red-50 border-red-200 text-red-700"
                                        : "bg-emerald-50 border-emerald-200 text-emerald-700"
                                }`}
                            >
                                <span>
                                    {conflictsError
                                        ? `Gagal cek konflik: ${conflictsError?.response?.data?.message ?? conflictsError?.message ?? "error"}`
                                        : conflictCheckPending
                                        ? conflictCheckPending
                                        : isLoadingConflicts
                                        ? "Memeriksa konflik diskon..."
                                        : conflictCount > 0
                                        ? `${conflictCount} produk konflik di periode ${from} — ${to}`
                                        : `Tidak ada konflik di periode ${from} — ${to}`}
                                </span>
                                <span className="opacity-70">
                                    {conflictCheckActive ? "cek konflik aktif" : "cek konflik nonaktif"}
                                </span>
                            </div>
                        )}

                        <div className="border border-neutral-200 rounded-xl overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-2 bg-neutral-50 border-b border-neutral-200">
                                <label className="flex items-center gap-2 text-xs font-medium text-neutral-700 cursor-pointer">
                                    <Checkbox
                                        checked={allVisibleSelected}
                                        onChange={toggleAllVisible}
                                        disabled={stepDisabled.target || rows.length === 0}
                                    />
                                    <span>{allVisibleSelected ? "Lepas Semua di Halaman" : "Pilih Semua di Halaman"}</span>
                                </label>
                                <div className="text-xs text-neutral-500">
                                    {level ? `Produk ${level}` : "Daftar produk"}
                                </div>
                            </div>

                            <div className="max-h-96 overflow-y-auto divide-y divide-neutral-100">
                                {!level && (
                                    <div className="px-4 py-6 text-xs text-center text-neutral-500">
                                        Pilih level terlebih dahulu untuk menampilkan produk.
                                    </div>
                                )}
                                {level && isLoadingProductList && (
                                    <div className="px-4 py-6 text-xs text-center text-neutral-500">Memuat...</div>
                                )}
                                {level && !isLoadingProductList && rows.length === 0 && (
                                    <div className="px-4 py-6 text-xs text-center text-neutral-500">{lang.no_data}</div>
                                )}
                                {level &&
                                    !isLoadingProductList &&
                                    rows.map((row) => {
                                        const selected = isSelected(row)
                                        const conflicts = getRowConflicts(row)
                                        const conflicted = !!conflicts
                                        const conflictNames = conflicted
                                            ? conflicts.map((c) => c.name).join(", ")
                                            : ""
                                        return (
                                            <label
                                                key={row.id}
                                                className={`flex items-center gap-3 px-4 py-3 transition ${
                                                    conflicted
                                                        ? "bg-neutral-50 opacity-60 cursor-not-allowed"
                                                        : selected
                                                        ? "bg-orange-50 cursor-pointer"
                                                        : "cursor-pointer hover:bg-neutral-50"
                                                }`}
                                            >
                                                <Checkbox
                                                    checked={selected}
                                                    onChange={() => toggleOne(row)}
                                                    disabled={conflicted}
                                                />
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <span className="text-sm font-medium text-neutral-900 truncate">
                                                        {targetTitle(row)}
                                                    </span>
                                                    {targetDescription(row) && (
                                                        <span className="text-xs text-neutral-500 truncate">
                                                            {targetDescription(row)}
                                                        </span>
                                                    )}
                                                    {conflicted && (
                                                        <span className="text-xs text-red-700 truncate">
                                                            Sudah ada diskon: {conflictNames}
                                                        </span>
                                                    )}
                                                </div>
                                            </label>
                                        )
                                    })}
                            </div>

                            {level && totalPages > 1 && (
                                <div className="flex items-center justify-between px-4 py-2 bg-neutral-50 border-t border-neutral-200 text-xs">
                                    <button
                                        type="button"
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        disabled={page <= 1 || isLoadingProductList}
                                        className="inline-flex items-center gap-1 px-3 py-1 transition rounded-full disabled:opacity-40 hover:bg-neutral-200"
                                    >
                                        <ArrowNarrowLeft className="w-4 h-4" />
                                        <span>Sebelumnya</span>
                                    </button>
                                    <span className="text-neutral-500">
                                        Halaman {page} dari {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={page >= totalPages || isLoadingProductList}
                                        className="inline-flex items-center gap-1 px-3 py-1 transition rounded-full disabled:opacity-40 hover:bg-neutral-200"
                                    >
                                        <span>Selanjutnya</span>
                                        <ArrowNarrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>

                        <Description error={errors.target} />

                        {target.length > 0 && (
                            <div className="border border-neutral-200 rounded-xl">
                                <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-200 text-xs font-medium text-neutral-700">
                                    Produk Terpilih ({target.length})
                                </div>
                                <div className="max-h-64 overflow-y-auto divide-y divide-neutral-100">
                                    {target.map((row) => {
                                        const conflicts = getRowConflicts(row)
                                        const conflictNames = conflicts
                                            ? conflicts.map((c) => c.name).join(", ")
                                            : ""
                                        return (
                                            <div
                                                key={row.id}
                                                className="flex items-center justify-between gap-3 px-4 py-2"
                                            >
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-sm text-neutral-900 truncate">
                                                        {targetTitle(row)}
                                                    </span>
                                                    {targetDescription(row) && (
                                                        <span className="text-xs text-neutral-500 truncate">
                                                            {targetDescription(row)}
                                                        </span>
                                                    )}
                                                    {conflicts && (
                                                        <span className="text-xs text-red-700 truncate">
                                                            Konflik dengan: {conflictNames}
                                                        </span>
                                                    )}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeTarget(row.id)}
                                                    className="inline-flex items-center p-1 text-white transition bg-red-800 rounded-full active:hover:scale-90"
                                                    aria-label="Hapus"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </FormSection>

            <div className="mt-8 text-xs text-right">
                <button
                    type="submit"
                    className="items-center px-6 py-3 text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
                >
                    <span>Terapkan</span>
                </button>
            </div>
        </form>
    )
}

function FormSection({ title, description, children }) {
    return (
        <div className="mt-10 sm:mt-0">
            <div className="md:grid md:grid-cols-3 md:gap-6">
                <div className="md:col-span-1">
                    <div className="px-4 sm:px-0">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">{title}</h3>
                        <p className="mt-1 text-sm text-gray-600">{description}</p>
                    </div>
                </div>
                <div className="mt-5 md:col-span-2 md:mt-0">
                    <div className="border sm:rounded-xl">
                        <div className="px-4 py-5 sm:p-6">{children}</div>
                    </div>
                </div>
            </div>
        </div>
    )
}
